import { useState, useEffect } from 'react';

// Configurable development day duration. Beta runs development time at 75% of
// the old x20 pacing: one development day takes 2m15s. Visual animations use
// their own timing and should not read this value.
// In dev mode, this can be overridden via localStorage.
const DEFAULT_DAY_MS = Math.floor(((1000 * 60 * 60) / 20) * 0.75);
const DEV_DAY_MS = 1000 * 10; // 10 seconds (dev default)

function getDayDurationMs(): number {
  if (import.meta.env.DEV) {
    const stored = localStorage.getItem('dev_day_duration_ms');
    if (stored) return parseInt(stored, 10);
    return DEV_DAY_MS;
  }
  return DEFAULT_DAY_MS;
}

export function setDevDayDuration(ms: number) {
  localStorage.setItem('dev_day_duration_ms', String(ms));
}

export function getDevDayDuration(): number {
  return getDayDurationMs();
}

export interface CountdownInfo {
  totalMs: number;
  remainingMs: number;
  progressPercent: number;
  daysComplete: number;
  daysRemaining: number;
  isComplete: boolean;
  formatted: string;
}

export function useProjectCountdown(
  startedAt: string | Date,
  constructionDays: number,
  dayDurationMs?: number
): CountdownInfo {
  const [now, setNow] = useState(Date.now());
  const dayMs = dayDurationMs ?? getDayDurationMs();

  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, []);

  const start = new Date(startedAt).getTime();
  const totalMs = constructionDays * dayMs;
  const elapsed = now - start;
  const remainingMs = Math.max(0, totalMs - elapsed);
  const progressPercent = Math.min(100, (elapsed / totalMs) * 100);
  const daysComplete = Math.min(constructionDays, elapsed / dayMs);
  const daysRemaining = constructionDays - daysComplete;
  const isComplete = remainingMs <= 0;

  // Format remaining time
  let formatted = '';
  if (isComplete) {
    formatted = 'Complete!';
  } else if (remainingMs < 60000) {
    formatted = `${Math.ceil(remainingMs / 1000)}s`;
  } else if (remainingMs < 3600000) {
    formatted = `${Math.ceil(remainingMs / 60000)}m`;
  } else {
    const h = Math.floor(remainingMs / 3600000);
    const m = Math.ceil((remainingMs % 3600000) / 60000);
    formatted = `${h}h ${m}m`;
  }

  return {
    totalMs,
    remainingMs,
    progressPercent,
    daysComplete,
    daysRemaining,
    isComplete,
    formatted,
  };
}
